'use client';
import { useState, useEffect } from 'react';
import { ProfileHeader } from '../components/ProfileHeader/ProfileHeader';
import { ProfileEditForm } from '../components/ProfileEditForm/ProfileEditForm';
import { SecurityCard } from '../components/SecurityCard/SecurityCard';
import { ProfileInfoCard } from '../components/ProfileInfoCard/ProfileInfoCard';
import { PreferencesCard } from '../components/PreferencesCard/PreferencesCard';
import { DangerZoneCard } from '../components/DangerZoneCard/DangerZoneCard';
import {
	ChangePasswordModal,
	DataExportModal,
	DeleteAccountModal,
	LoadingState,
	useToast,
} from '@practium/ui';
import { ConfirmationModal } from '../components/Modals/ConfirmationModal/ConfirmationModal';
import { useNotificationPreferences, useProfileForm } from '../hooks';
import type { ProfileData } from '../types/profile.types';
import styles from './ProfileEditPage.module.css';
import { ProfileEditData } from '../schemas/profile.schema';

interface ProfileEditPageProps {
	data: ProfileData | null;
	isLoading?: boolean;
	onSave: (data: ProfileEditData) => Promise<void>;
	onChangePassword: (currentPassword: string, newPassword: string) => Promise<void>;
	onExportData: () => Promise<void>;
	onDeleteAccount: () => Promise<void>;
	onUpgrade?: () => void;
}

export const ProfileEditPage = ({
	data,
	isLoading,
	onSave,
	onChangePassword,
	onExportData,
	onDeleteAccount,
}: ProfileEditPageProps) => {
	const toast = useToast();
	const { notificationsEnabled, toggleNotifications } = useNotificationPreferences();
	const [isSaving, setIsSaving] = useState(false);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [showExportModal, setShowExportModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const [avatarPreview, setAvatarPreview] = useState<string | undefined>(
		data?.user?.avatar?.url
	);
	const [backgroundPreview, setBackgroundPreview] = useState<string | undefined>(
		data?.user?.background?.url
	);
	const [avatarFile, setAvatarFile] = useState<File | undefined>();
	const [backgroundFile, setBackgroundFile] = useState<File | undefined>();

	useEffect(() => {
		if (!avatarFile) setAvatarPreview(data?.user?.avatar?.url);
	}, [data, avatarFile]);

	useEffect(() => {
		if (!backgroundFile) {
			setBackgroundPreview(data?.user?.background?.url);
		}
	}, [data, backgroundFile]);

	const { formData, errors, touched, handleChange, handleBlur, validateAll } = useProfileForm({
		initialData: data ?? undefined,
	});

	if (isLoading || !data) {
		return <LoadingState fullScreen />;
	}

	const handleAvatarChange = (file: File, preview: string) => {
		setAvatarFile(file);
		setAvatarPreview(preview);
	};

	const handleBackgroundChange = (file: File, preview: string) => {
		setBackgroundFile(file);
		setBackgroundPreview(preview);
	};

	const handleSaveClick = () => {
		const isValid = validateAll();

		if (!isValid) {
			toast.error('Por favor, corrija os erros no formulário antes de salvar');
			return;
		}

		setShowSaveModal(true);
	};

	const handleConfirmSave = async () => {
		setIsSaving(true);
		try {
			await onSave({
				formData,
				avatarFile,
				backgroundFile,
			});
		} finally {
			setIsSaving(false);
			setShowSaveModal(false);
		}
	};

	const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
		await onChangePassword(currentPassword, newPassword);
		setShowPasswordModal(false);
	};

	const handleDataExport = async () => {
		await onExportData();
		setShowExportModal(false);
	};

	const handleAccountDelete = async () => {
		await onDeleteAccount();
		setShowDeleteModal(false);
	};

	const originalName = `${data?.user?.name || ''} ${data?.user?.surName || ''}`.trim();
	const originalEmail = data?.user?.email || '';

	return (
		<div className={styles.profileEditPage}>
			<div className={styles.headerWrapper}>
				<ProfileHeader
					name={originalName}
					email={originalEmail}
					avatar={avatarPreview}
					backgroundUrl={backgroundPreview}
					isEditMode={true}
					onSave={handleSaveClick}
					onAvatarChange={handleAvatarChange}
					onBackgroundChange={handleBackgroundChange}
					isLoading={isSaving}
				/>
			</div>

			<div className={styles.container}>
				<div className={styles.contentGrid}>
					<main className={styles.leftColumn}>
						<ProfileEditForm
							formData={formData}
							errors={errors}
							touched={touched}
							isLoading={isSaving}
							onChange={handleChange}
							onBlur={handleBlur}
						/>
					</main>
					<aside className={styles.rightColumn}>
						<ProfileInfoCard />
						<SecurityCard
							onChangePassword={() => setShowPasswordModal(true)}
							onExportData={() => setShowExportModal(true)}
							isLoading={isSaving}
						/>
						<PreferencesCard
							notificationsEnabled={notificationsEnabled}
							onToggleNotifications={toggleNotifications}
						/>
						<DangerZoneCard onDelete={() => setShowDeleteModal(true)} isLoading={isSaving} />
					</aside>
				</div>
			</div>

			<ConfirmationModal
				isOpen={showSaveModal}
				title="Confirmar alterações"
				message="Tem certeza que deseja salvar as alterações realizadas no seu perfil?"
				confirmText="Salvar"
				cancelText="Cancelar"
				onConfirm={handleConfirmSave}
				onCancel={() => setShowSaveModal(false)}
				isLoading={isSaving}
			/>

			<ChangePasswordModal
				isOpen={showPasswordModal}
				onClose={() => setShowPasswordModal(false)}
				onConfirm={handlePasswordChange}
				isLoading={isSaving}
				userEmail={originalEmail}
			/>

			<DataExportModal
				isOpen={showExportModal}
				onClose={() => setShowExportModal(false)}
				onConfirm={handleDataExport}
				isLoading={isSaving}
			/>

			<DeleteAccountModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleAccountDelete}
				isLoading={isSaving}
			/>
		</div>
	);
};
